import React, { useEffect, useState, useRef } from "react";
import "./styles.scss";
import AwbContainer from "./AwbContainer";
import MultiInput from "./MultiInput";
import AwbTrackingList from "./AwbTrackingList";
import { useParams, useSearchParams } from "react-router-dom";
import { getTrackingUrl } from "../../config/api-config";

function ShipmentTracker() {
  const { trackingId } = useParams();
  const [searchParams] = useSearchParams();
  const [awbNumbers, setAwbNumbers] = useState([]);
  const [trackingResults, setTrackingResults] = useState([]);
  const [activeTracking, setActiveTracking] = useState(null);
  const [activeOrderInformation, setActiveOrderInformation] = useState(null);
  const [activeTrackingList, setActiveTrackingList] = useState([]);
  const [switchChildShipment, setSwitchChildShipment] = useState(false);
  const initialLoadRef = useRef(false);

  useEffect(() => {
    // Check for query parameters first
    const dParam = searchParams.get('d');
    const shipmentNoParam = searchParams.get('shipmentNo');
    
    // Priority: query parameters > route parameter
    const trackingNumber = dParam || shipmentNoParam || trackingId;
    
    // Only load once on initial mount if tracking number exists
    if (trackingNumber && !initialLoadRef.current) {
      handleAddAwb(trackingNumber);
      initialLoadRef.current = true;
    }
  }, [trackingId, searchParams]);

  const handleTrackingApi = async (awbNumber) => {
    try {
      const response = await fetch(getTrackingUrl(awbNumber));
      const data = await response.json();
      return data;
    } catch (error) {
      return null;
    }
  };

  const handleAddAwb = async (value) => {
    if (!value) return;

    // Clear existing data when tracking new orders
    setAwbNumbers([]);
    setTrackingResults([]);
    setActiveTracking(null);
    setActiveOrderInformation(null);
    setActiveTrackingList([]);

    let cleanedAwbNumbers = [...new Set(value.trim().split(/\s+/))];

    //Total number of AWB added at once
    if (cleanedAwbNumbers.length > 0) {
      let addNewAwb = true;

      if (cleanedAwbNumbers.length === 1) {
        addNewAwb = await getAwbNumberDetails(cleanedAwbNumbers[0]);
        setActiveTracking(cleanedAwbNumbers[0]);
      } else {
        let newData = await Promise.all(
          cleanedAwbNumbers.map(async (awb) => await handleTrackingApi(awb))
        );

        newData = newData.filter(
          (data) => data && data?.statuses && data.statuses.length > 0
        );
        cleanedAwbNumbers = newData
          .map(
            (i) => i.orderInformation?.trackingId || i.statuses?.[0]?.trackingId
          )
          .filter(Boolean);

        setTrackingResults(newData);
        setActiveTracking(cleanedAwbNumbers[cleanedAwbNumbers.length - 1]);
        setActiveOrderInformation(newData[newData.length - 1].orderInformation);
        setActiveTrackingList(
          await processStatuses(
            newData[newData.length - 1]?.statuses || [],
            newData[newData.length - 1]?.orderInformation?.trackingId ||
            newData[newData.length - 1]?.statuses?.[0]?.trackingId
          )
        );
      }

      if (addNewAwb) {
        setAwbNumbers(cleanedAwbNumbers);
      }
    }
  };

  const getAwbNumberDetails = async (awbNumber) => {
    const data = await handleTrackingApi(awbNumber);
    if (!data?.statuses || data.statuses.length === 0) {
      return false;
    } else {
      setTrackingResults([data]);
      setActiveTrackingList(
        await processStatuses(data?.statuses || [], awbNumber)
      );
      setActiveOrderInformation(data.orderInformation || null);
      return true;
    }
  };

  async function processStatuses(statuses) {
    const inputStatuses = Array.isArray(statuses) ? [...statuses] : [];
    const groupedData = {};
    let podImage = null;

    inputStatuses.forEach((status) => {
      const type = status.category.toLowerCase();
      if (!groupedData[type]) {
        groupedData[type] = [];
      }
      groupedData[type].push(status);
    });

    // Convert to the required format
    const result = Object.keys(groupedData).map((type) => {
      if (type == "delivered") {
        return {
          type,
          items: groupedData[type],
          podImage: podImage,
        };
      }
      return {
        type,
        items: groupedData[type],
      };
    });
    return result;
  }

  const getCurrentChildShipment = async (childAwbNumber) => {
    const data = await handleTrackingApi(childAwbNumber);
    if (!data?.statuses || data.statuses.length === 0) {
      return false;
    } else {
      setActiveTrackingList(
        await processStatuses(data?.statuses || [], childAwbNumber)
      );
    }
  };

  const setActiveTrackingId = async (item) => {
    setActiveTracking(item?.trackingId);
    const trackingResult = trackingResults.find(
      (i) =>
        i?.orderInformation?.trackingId === item?.trackingId ||
        i?.statuses?.[0]?.trackingId === item?.trackingId
    );
    setActiveOrderInformation(trackingResult?.orderInformation || null);
    setActiveTrackingList(
      await processStatuses(item?.statuses || [], item?.trackingId)
    );
  };

  const removeAwbNumber = async (e) => {
    const newTrackingResults = trackingResults.filter(
      (i) =>
        i.orderInformation?.trackingId !== e &&
        i.orderInformation?.cAwbNumber !== e &&
        i.statuses?.[0]?.trackingId !== e &&
        i.statuses?.[0]?.cAwbNumber !== e
    );

    setTrackingResults(newTrackingResults);
    setAwbNumbers(awbNumbers.filter((i) => i != e));

    if (newTrackingResults.length == 0) {
      setActiveTracking(null);
      setActiveOrderInformation(null);
      setActiveTrackingList([]);
      return;
    }

    if (activeTracking === e) {
      const firstResult = newTrackingResults[0];
      const trackingId =
        firstResult?.orderInformation?.trackingId ||
        firstResult?.statuses?.[0]?.trackingId;
      setActiveTracking(trackingId);
      setActiveTrackingList(
        await processStatuses(firstResult?.statuses || [], trackingId)
      );
    }
  };

  const clearAllAwbNumbers = () => {
    setAwbNumbers([]);
    setTrackingResults([]);
    setActiveTracking(null);
    setActiveOrderInformation(null);
    setActiveTrackingList([]);
  };

  return (
    <>
      <div className="tracking-container" style={{ justifyContent: "center" }}>
        <div className="tracking-main-wrapper">
          <MultiInput
            awbNumbers={awbNumbers}
            removeAwbNumber={removeAwbNumber}
            handleAddAwb={handleAddAwb}
            trackingResults={trackingResults}
            activeTracking={activeTracking}
            setActiveTrackingId={setActiveTrackingId}
            clearAllAwbNumbers={clearAllAwbNumbers}
          />
          {/* Hidden legacy components - keeping for reference */}
          {false && (
            <>
              <AwbContainer
                trackingResults={trackingResults}
                activeTracking={activeTracking}
                setActiveTrackingId={setActiveTrackingId}
              />
              <AwbTrackingList
                getCurrentChildShipment={getCurrentChildShipment}
                activeOrderInformation={activeOrderInformation}
                activeTracking={activeTracking}
                trackingResults={trackingResults}
                activeTrackingList={activeTrackingList}
                switchChildShipment={switchChildShipment}
                setSwitchChildShipment={setSwitchChildShipment}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default ShipmentTracker;
